import { Directive, ElementRef, HostListener, inject, output } from '@angular/core';
import { DateTime } from 'luxon';

@Directive({
    selector: '[libDateInputMask]'
})
export class DateInputMaskDirective {
    private readonly elementRef = inject(ElementRef) as ElementRef<HTMLInputElement>;

    private readonly format = 'MM/dd/yyyy';
    private readonly separatorIndexes = new Set([2, 5]);
    private readonly editableIndexes = [0, 1, 3, 4, 6, 7, 8, 9];
    private readonly defaultMask = '00/00/0000';
    private readonly minDate = DateTime.fromFormat('12/31/1969', 'MM/dd/yyyy');

    readonly keyboardDateBlur = output<boolean>();
    readonly keyboardDateChange = output<string>();

    @HostListener('focus')
    onFocus(): void {
        this.syncInputValue();
    }

    @HostListener('click')
    onClick(): void {
        this.fixCaretOnSeparator();
    }

    @HostListener('input')
    onInput(): void {
        this.syncInputValue();
        this.keyboardDateChange.emit(this.elementRef.nativeElement.value);
    }

    @HostListener('keydown', ['$event'])
    onKeyDown(event: KeyboardEvent): void {
        const input = this.elementRef.nativeElement;
        const selectionStart = input.selectionStart ?? 0;
        const selectionEnd = input.selectionEnd ?? selectionStart;

        if (this.shouldSkipCustomHandling(event)) {
            return;
        }

        if (event.key >= '0' && event.key <= '9') {
            event.preventDefault();
            this.syncInputValue();
            this.typeDigit(event.key, selectionStart, selectionEnd);
            this.keyboardDateChange.emit(input.value);
            return;
        }

        if (event.key === 'Backspace' || event.key === 'Delete') {
            event.preventDefault();
            this.syncInputValue();
            this.deleteDigit(event.key, selectionStart, selectionEnd);
            this.keyboardDateChange.emit(input.value);
            return;
        }

        if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            this.moveWithArrow(event.key, selectionStart, selectionEnd);
            return;
        }

        if (event.key === 'Home' || event.key === 'End') {
            setTimeout(() => this.fixCaretOnSeparator(), 0);
            return;
        }

        if (event.key === '/') {
            event.preventDefault();
            if (!this.elementRef.nativeElement.value) {
                return;
            }
            this.setCaret(this.nextEditablePos(selectionEnd, 1));
            return;
        }

        if (event.key.length === 1) {
            event.preventDefault();
        }
    }

    @HostListener('blur')
    onBlur(): void {
        this.syncInputValue();
        const input = this.elementRef.nativeElement;
        let inputValue = input.value;
        if (this.isZeroMask(inputValue)) {
            input.value = '';
            inputValue = '';
        }
        this.keyboardDateBlur.emit(this.isValidDate(inputValue) && this.hasExactFormat(inputValue));
    }

    private shouldSkipCustomHandling(event: KeyboardEvent): boolean {
        if (event.ctrlKey || event.metaKey || event.altKey) {
            return true;
        }

        const allowedKeys = ['Tab', 'Escape', 'Enter'];
        return allowedKeys.includes(event.key);
    }

    private syncInputValue(): void {
        const input = this.elementRef.nativeElement;
        const raw = input.value;
        if (!raw?.trim()) {
            input.value = '';
            return;
        }

        let next = this.toMask(raw);
        if (!next) {
            input.value = '';
            return;
        }

        next = this.clampMonth(next);
        next = this.clampDay(next);

        input.value = next;
    }

    private fixCaretOnSeparator(): void {
        const input = this.elementRef.nativeElement;
        if (!input.value) {
            return;
        }

        const selectionStart = input.selectionStart;
        const selectionEnd = input.selectionEnd;

        if (selectionStart === null || selectionEnd === null || selectionStart !== selectionEnd) {
            return;
        }

        if (this.separatorIndexes.has(selectionStart)) {
            this.setCaret(this.nextEditablePos(selectionStart, 1));
        }
    }

    private typeDigit(digit: string, selectionStart: number, selectionEnd: number): void {
        const input = this.elementRef.nativeElement;
        const base = input.value.length === 10 ? input.value : this.defaultMask;
        const chars = base.split('');

        if (selectionStart !== selectionEnd) {
            this.clearSelectedDigits(chars, selectionStart, selectionEnd);
        }

        const targetIndex = this.getTypePos(selectionStart, selectionEnd);
        if (targetIndex === null) {
            return;
        }
        if (!this.isEditablePos(targetIndex)) {
            return;
        }

        if (targetIndex === 0 && digit > '1') {
            chars[0] = '0';
            chars[1] = digit;
            this.clampMonthChars(chars);
            this.clampDayChars(chars);
            this.writeChars(input, chars);
            this.setCaret(3);
            return;
        }

        if (targetIndex === 3 && digit > '3') {
            chars[3] = '0';
            chars[4] = digit;
            this.clampMonthChars(chars);
            this.clampDayChars(chars);
            this.writeChars(input, chars);
            this.setCaret(6);
            return;
        }

        chars[targetIndex] = digit;
        this.clampMonthChars(chars);
        this.clampDayChars(chars);
        this.writeChars(input, chars);
        this.setCaret(this.nextEditablePos(targetIndex + 1, 1));
    }

    private deleteDigit(key: 'Backspace' | 'Delete', selectionStart: number, selectionEnd: number): void {
        const input = this.elementRef.nativeElement;
        if (input.value.length !== 10) {
            input.value = '';
            return;
        }

        const chars = input.value.split('');

        if (selectionStart !== selectionEnd) {
            this.clearSelectedDigits(chars, selectionStart, selectionEnd);
            this.clampMonthChars(chars);
            this.clampDayChars(chars);
            this.writeChars(input, chars);
            if (this.isZeroMask(input.value)) {
                input.value = '';
                this.setCaret(0);
                return;
            }
            this.setCaret(this.nextEditablePos(selectionStart, 1));
            return;
        }

        const targetIndex =
            key === 'Backspace'
                ? this.nextEditablePos(Math.max(selectionStart - 1, 0), -1)
                : this.nextEditablePos(selectionStart, 1);

        if (targetIndex === null) {
            return;
        }
        if (!this.isEditablePos(targetIndex)) {
            return;
        }

        chars[targetIndex] = '0';
        this.clampMonthChars(chars);
        this.clampDayChars(chars);
        this.writeChars(input, chars);
        if (this.isZeroMask(input.value)) {
            input.value = '';
            this.setCaret(0);
            return;
        }
        this.setCaret(targetIndex);
    }

    private writeChars(input: HTMLInputElement, chars: string[]): void {
        const joined = chars.join('');
        input.value = joined;
    }

    private clearSelectedDigits(chars: string[], selectionStart: number, selectionEnd: number): void {
        for (let index = selectionStart; index < selectionEnd; index += 1) {
            if (this.isEditablePos(index)) {
                chars[index] = '0';
            }
        }
    }

    private getTypePos(selectionStart: number, selectionEnd: number): number | null {
        if (selectionStart !== selectionEnd) {
            return this.nextEditablePos(selectionStart, 1);
        }

        if (this.isEditablePos(selectionStart)) {
            return selectionStart;
        }

        return this.nextEditablePos(selectionStart, 1);
    }

    private moveWithArrow(key: 'ArrowLeft' | 'ArrowRight', selectionStart: number, selectionEnd: number): void {
        if (!this.elementRef.nativeElement.value) {
            return;
        }

        if (selectionStart !== selectionEnd) {
            const caret = key === 'ArrowLeft' ? selectionStart : selectionEnd;
            this.setCaret(caret);
            return;
        }

        if (key === 'ArrowLeft') {
            const leftStart = Math.max(selectionStart - 1, 0);
            this.setCaret(this.nextEditablePos(leftStart, -1));
            return;
        }

        this.setCaret(this.nextEditablePos(selectionStart + 1, 1));
    }

    private isEditablePos(index: number): boolean {
        return this.editableIndexes.includes(index);
    }

    private nextEditablePos(startIndex: number, direction: 1 | -1): number | null {
        if (direction === 1) {
            for (const index of this.editableIndexes) {
                if (index >= startIndex) {
                    return index;
                }
            }
            return this.defaultMask.length;
        }

        for (let i = this.editableIndexes.length - 1; i >= 0; i -= 1) {
            const index = this.editableIndexes[i];
            if (index <= startIndex) {
                return index;
            }
        }

        return this.editableIndexes[0] ?? null;
    }

    private setCaret(index: number | null): void {
        if (index === null) {
            return;
        }
        this.elementRef.nativeElement.setSelectionRange(index, index);
    }

    private toMask(rawValue: string): string {
        if (!rawValue?.trim()) {
            return '';
        }

        const digits = rawValue.replaceAll(/\D/g, '').slice(0, 8);
        if (!digits.length) {
            return '';
        }

        const paddedDigits = digits.padEnd(8, '0');

        return `${paddedDigits.slice(0, 2)}/${paddedDigits.slice(2, 4)}/${paddedDigits.slice(4, 8)}`;
    }

    /** Month segment (MM) must not exceed 12. */
    private clampMonthChars(chars: string[]): void {
        if (chars.length < 10) {
            return;
        }
        const month = Number.parseInt(chars[0] + chars[1], 10);
        if (Number.isNaN(month) || month <= 12) {
            return;
        }
        chars[0] = '1';
        chars[1] = '2';
    }

    private clampMonth(mask: string): string {
        if (mask.length !== 10) {
            return mask;
        }
        const chars = mask.split('');
        this.clampMonthChars(chars);
        return chars.join('');
    }

    private clampDayChars(chars: string[]): void {
        if (chars.length < 10) {
            return;
        }
        const day = Number.parseInt(chars[3] + chars[4], 10);
        if (Number.isNaN(day) || day <= 31) {
            return;
        }
        chars[3] = '3';
        chars[4] = '1';
    }

    private clampDay(mask: string): string {
        if (mask.length !== 10) {
            return mask;
        }
        const chars = mask.split('');
        this.clampDayChars(chars);
        return chars.join('');
    }

    private isZeroMask(value: string): boolean {
        return value === this.defaultMask;
    }

    private isValidDate(date: string): boolean {
        if (!date?.trim() || this.isZeroMask(date)) {
            return false;
        }

        const formattedDate = DateTime.fromFormat(date, this.format);
        return formattedDate.isValid && formattedDate > this.minDate;
    }

    private hasExactFormat(date: string): boolean {
        if (!date?.trim()) {
            return false;
        }
        const formattedDate = DateTime.fromFormat(date, this.format);
        return formattedDate.isValid && formattedDate.toFormat(this.format) === date;
    }
}
